import { baseApi } from 'src/Services/api.ts'

export enum DocumentTypeEnum {
  CONTRACT = 'CONTRACT',
  INVOICE = 'INVOICE',
}

export enum DocumentStatusEnum {
  DRAFT = 'DRAFT',
  ON_APPROVAL = 'ON_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum ApprovalStatusEnum {
  PENDING = 'PENDING',
  IN_PROCESS = 'IN_PROCESS',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  SKIPPED = 'SKIPPED',
}

export interface User {
  id: number
  email: string
  firstName?: string
  lastName?: string
}

export interface ApprovalHistoryItem {
  id: string
  user: {
    id: number
    name: string
    email: string
  }
  status: ApprovalStatusEnum
  order: number
  startDate: string | null
  deadline: string | null
  completedAt: string | null
  comment: string | null
}

export interface InboxDocument {
  id: string
  type: DocumentTypeEnum
  status: DocumentStatusEnum
  contract: {
    id: number
    contractNumber: string | null
    clientName: string
    googleDocsUrl: string | null
  } | null
  approvalItem: {
    id: string
    status: ApprovalStatusEnum
    startDate: string | null
    deadline: string | null
    order: number
  }
  createdAt: string
}

export interface InboxQueryParams {
  page?: number
  limit?: number
  status?: ApprovalStatusEnum
}

export interface SendForApprovalBody {
  approvers: Array<{
    userId: number
    deadline?: string
  }>
}

export interface ApproveDocumentBody {
  comment?: string
}

export interface RejectDocumentBody {
  comment: string
  continueFromOrder?: number
}

export interface ResendForApprovalBody {
  startFromOrder: number
}

export interface InboxDocumentsResponse {
  documents: InboxDocument[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export const documentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Получить входящие документы для согласования
    getInboxDocuments: builder.query<
      InboxDocumentsResponse,
      InboxQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams()

        if (params) {
          if (params.page) queryParams.append('page', params.page.toString())
          if (params.limit) queryParams.append('limit', params.limit.toString())
          if (params.status) queryParams.append('status', params.status)
        }

        const queryString = queryParams.toString()
        return `/documents/inbox${queryString ? `?${queryString}` : ''}`
      },
      providesTags: ['documents'],
    }),

    // Получить историю согласования документа
    getApprovalHistory: builder.query<
      { history: ApprovalHistoryItem[] },
      string
    >({
      query: (documentId) => `/documents/${documentId}/approval-history`,
      providesTags: ['documents'],
    }),

    // Отправить контракт на согласование
    sendForApproval: builder.mutation<
      { documentId: string },
      { contractId: number; data: SendForApprovalBody }
    >({
      query: ({ contractId, data }) => ({
        url: `/contracts/${contractId}/send-for-approval`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['documents', 'contracts'],
    }),

    // Пропустить согласование
    skipApproval: builder.mutation<{ documentId: string }, number>({
      query: (contractId) => ({
        url: `/contracts/${contractId}/skip-approval`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['documents', 'contracts'],
    }),

    // Согласовать документ
    approveDocument: builder.mutation<
      { success: boolean; message: string },
      { documentId: string; data?: ApproveDocumentBody }
    >({
      query: ({ documentId, data }) => ({
        url: `/documents/${documentId}/approve`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: ['documents', 'contracts'],
    }),

    // Отклонить документ
    rejectDocument: builder.mutation<
      { success: boolean; message: string },
      { documentId: string; data: RejectDocumentBody }
    >({
      query: ({ documentId, data }) => ({
        url: `/documents/${documentId}/reject`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['documents', 'contracts'],
    }),

    // Повторно отправить документ на согласование
    resendForApproval: builder.mutation<
      { success: boolean; message: string },
      { documentId: string; data: ResendForApprovalBody }
    >({
      query: ({ documentId, data }) => ({
        url: `/documents/${documentId}/resend-for-approval`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['documents', 'contracts'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetInboxDocumentsQuery,
  useGetApprovalHistoryQuery,
  useSendForApprovalMutation,
  useSkipApprovalMutation,
  useApproveDocumentMutation,
  useRejectDocumentMutation,
  useResendForApprovalMutation,
} = documentsApi
